'use client';

import { useDebouncedValue } from '@/lib/hooks/use-debounced-value';
import { isOpenBoard, useLeadBoard } from '@/lib/services/leads';
import type { LeadBoardRow } from '@/lib/services/leads';

const ENQUIRY_SEARCH_LIMIT = 5;

export type { LeadBoardRow };

/**
 * Global-search lane for enquiries.
 *
 * Reads the **In-progress** board only (`outcome` defaults to `open`), which is
 * the live pipeline. The two terminal tabs are deliberately not searched: a
 * Retained enquiry has already become a matter and is found by the matter lane
 * — listing it here would put the same person on screen twice under two
 * identities — and a Not-proceeding enquiry is closed history rather than
 * something to jump to. The board applies `q` as a SQL filter over the four
 * fields the row renders as its identity, so the response is bounded by the
 * match rather than by the firm's whole pipeline.
 *
 * `leadsFeatureEnabled` is a required argument rather than a provider read
 * inside the hook, matching `useContactSearch` — and it is load-bearing, not
 * cosmetic: `GET /leads` returns a bare 404 for a firm without the `leads`
 * flag, so an ungated query would fire a failing request on every search.
 */
export function useEnquirySearch(query: string, leadsFeatureEnabled: boolean) {
  const debouncedQuery = useDebouncedValue(query, 300);
  const enabled = leadsFeatureEnabled && debouncedQuery.length >= 2;

  const { data, isLoading } = useLeadBoard({ q: debouncedQuery }, { enabled });

  const enquiries =
    enabled && data && isOpenBoard(data)
      ? data.sections
          .flatMap((section) => section.rows)
          .slice(0, ENQUIRY_SEARCH_LIMIT)
      : [];

  return {
    enquiries,
    isLoading: enabled && isLoading
  };
}
